import { useCallback, useEffect, useRef, useState } from "react";
import type {
  Classifications,
  FaceLandmarker,
  NormalizedLandmark,
} from "@mediapipe/tasks-vision";
import { getFaceLandmarker } from "./faceLandmarkerLoader";

/*
 * КАМЕРА ДЛЯ ВХОДА ПО ЛИЦУ — ПОРТ `useFaceLivenessDetection` ИЗ ВЕБ-ВИТРИНЫ.
 *
 * Витрина (`sc-frontend-atyrau`, Vue) берёт библиотеку Adilet как есть:
 * `src/libs/biometric-liveness`. Здесь React, и Vue-композабл напрямую не
 * встанет, поэтому перенесена ЛОГИКА — те же пороги, та же оценка кадра, тот
 * же выбор лучшего снимка, — а разметка своя, под планшет. Разойтись с
 * витриной по числам значило бы, что на iPad лицо «не помещается в овал» там,
 * где на сайте помещается. Меняя пороги здесь, сверяйтесь с `types.ts` той
 * библиотеки.
 *
 * Что сознательно не перенесено: сетка лица поверх видео и проверка резкости и
 * яркости — в витрине они выключены по умолчанию, и на входе не нужны.
 *
 * ЛУЧШИЙ КАДР, А НЕ ПОСЛЕДНИЙ. Пока человек держит лицо в овале, каждый кадр
 * получает оценку (моргание, поворот, центровка, размер, открытый рот), и
 * снимок переписывается, только когда оценка лучше. Отправляется лучший —
 * у CompreFace на нём самое высокое сходство.
 */

const NOSE_TIP = 1;
const LEFT_CHEEK = 234;
const RIGHT_CHEEK = 454;
const CHIN = 152;

/** Те же значения, что `getDefaultBiometricConfig()` библиотеки Adilet. */
const CONFIG = {
  prepDurationMs: 3000,
  holdDurationMs: 3000,
  holdDurationMinMs: 500,
  detectionTimeoutMs: 45000,
  detectionIntervalMs: 100,
  faceSizeMin: 0.17,
  faceSizeMax: 0.85,
  faceWidthIdeal: 0.34,
  centerThreshold: 0.22,
  straightThreshold: 0.09,
  chinMinVisible: 0.92,
  excellentScoreThreshold: 0.5,
  plateauFrames: 8,
  weights: {
    blink: 5.0,
    rotation: 3.0,
    centerX: 2.0,
    centerY: 1.5,
    size: 1.0,
    jawOpen: 0.5,
  },
  blinkClosedThreshold: 0.5,
  blinkOpenThreshold: 0.2,
  hintDebounceMs: 320,
} as const;

const HOLD_FRAMES_FULL = Math.ceil(
  CONFIG.holdDurationMs / CONFIG.detectionIntervalMs,
);
const HOLD_FRAMES_MIN = Math.ceil(
  CONFIG.holdDurationMinMs / CONFIG.detectionIntervalMs,
);

export type CapturePhase =
  | "loading"
  | "prep"
  | "position"
  | "blink"
  | "hold"
  | "done"
  | "error";

export type HintTone = "neutral" | "info" | "warn" | "success";

export interface FaceCaptureState {
  phase: CapturePhase;
  /** 0..100 — загрузка модели, пока `phase === "loading"`. */
  loadProgress: number;
  /** 0..100 — сколько осталось держать лицо неподвижно. */
  holdPercent: number;
  hint: string;
  tone: HintTone;
  error: string | null;
}

export interface UseFaceCaptureOptions {
  /** Попросить моргнуть перед снимком. От фото спасает, от видео — нет. */
  requireBlink?: boolean;
  onCaptured: (image: string) => void;
}

const TEXT = {
  loadingCamera: "Запуск камеры...",
  loadingModel: "Загрузка модели распознавания лица...",
  preparing: "Подготовка камеры...",
  placeFace: "Поместите лицо в овал",
  lookStraight: "Смотрите прямо в камеру и не двигайтесь",
  blink: "Моргните — подтвердите, что вы живой человек",
  done: "Готово — проверяем лицо",
  noFace: "Лицо не обнаружено — встаньте перед камерой",
  moveCloser: "Подойдите ближе к камере",
  moveBack: "Отойдите немного подальше от камеры",
  center: "Поместите лицо в центр овала",
  chin: "Поместите лицо полностью в овал",
  turn: "Посмотрите прямо в камеру",
  multipleFaces: "В кадре должно быть только одно лицо",
  cameraError: "Нет доступа к камере. Разрешите камеру в настройках браузера.",
  insecure:
    "Камера работает только по защищённому адресу (https). Откройте панель по https.",
  initError: "Не удалось запустить распознавание лица",
  timeout: "Не удалось зафиксировать лицо. Нажмите «Повторить».",
} as const;

type Hint = { text: string; tone: HintTone } | null;

function faceWidth(lm: NormalizedLandmark[]): number {
  return Math.abs(lm[RIGHT_CHEEK]!.x - lm[LEFT_CHEEK]!.x);
}

function noseRelativeX(lm: NormalizedLandmark[]): number {
  const center = (lm[LEFT_CHEEK]!.x + lm[RIGHT_CHEEK]!.x) / 2;
  const width = faceWidth(lm);
  return width === 0 ? 0 : (lm[NOSE_TIP]!.x - center) / width;
}

function isInOval(lm: NormalizedLandmark[]): boolean {
  const w = faceWidth(lm);
  const nose = lm[NOSE_TIP]!;
  return (
    w >= CONFIG.faceSizeMin &&
    w <= CONFIG.faceSizeMax &&
    Math.abs(nose.x - 0.5) <= CONFIG.centerThreshold &&
    Math.abs(nose.y - 0.5) <= CONFIG.centerThreshold &&
    lm[CHIN]!.y <= CONFIG.chinMinVisible
  );
}

function isStraight(lm: NormalizedLandmark[]): boolean {
  return Math.abs(noseRelativeX(lm)) < CONFIG.straightThreshold;
}

function blendshape(b: Classifications | undefined, name: string): number {
  return b?.categories.find((c) => c.categoryName === name)?.score ?? 0;
}

function eyesClosedScore(b: Classifications | undefined): number {
  return Math.max(blendshape(b, "eyeBlinkLeft"), blendshape(b, "eyeBlinkRight"));
}

/** Оценка кадра, меньше — лучше. Моргание весит больше всего: снимок с закрытыми глазами хуже любого. */
function frameScore(
  lm: NormalizedLandmark[],
  b: Classifications | undefined,
): number {
  const nose = lm[NOSE_TIP]!;
  const w = CONFIG.weights;
  return (
    w.blink * eyesClosedScore(b) +
    w.rotation * Math.abs(noseRelativeX(lm)) +
    w.centerX * Math.abs(nose.x - 0.5) +
    w.centerY * Math.abs(nose.y - 0.5) +
    w.size * Math.abs(faceWidth(lm) - CONFIG.faceWidthIdeal) +
    w.jawOpen * blendshape(b, "jawOpen")
  );
}

/** Главная проблема положения лица, по порядку важности. */
function positionHint(lm: NormalizedLandmark[] | undefined): Hint {
  if (!lm) return { text: TEXT.noFace, tone: "warn" };
  const w = faceWidth(lm);
  if (w < CONFIG.faceSizeMin) return { text: TEXT.moveCloser, tone: "warn" };
  if (w > CONFIG.faceSizeMax) return { text: TEXT.moveBack, tone: "warn" };
  const nose = lm[NOSE_TIP]!;
  if (
    Math.abs(nose.x - 0.5) > CONFIG.centerThreshold ||
    Math.abs(nose.y - 0.5) > CONFIG.centerThreshold
  ) {
    return { text: TEXT.center, tone: "info" };
  }
  if (lm[CHIN]!.y > CONFIG.chinMinVisible) return { text: TEXT.chin, tone: "info" };
  if (!isStraight(lm)) return { text: TEXT.turn, tone: "info" };
  return null;
}

/**
 * Камера, детектор и выбор кадра. Отдаёт состояние для разметки и `videoRef`,
 * который надо повесить на `<video>`.
 *
 * Камера включается `start()` и выключается сама: снимком, таймаутом,
 * размонтированием. Модель MediaPipe общая на всё приложение и не закрывается.
 */
export function useFaceCapture({ requireBlink = false, onCaptured }: UseFaceCaptureOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;

  const [state, setState] = useState<FaceCaptureState>({
    phase: "loading",
    loadProgress: 0,
    holdPercent: 0,
    hint: TEXT.loadingCamera,
    tone: "neutral",
    error: null,
  });

  /*
   * Всё, что меняется десять раз в секунду, живёт в ref, а не в state: цикл
   * детекции читает и пишет это на каждом кадре, и перерисовка на каждый кадр
   * ничего бы не показала, кроме нагрузки на iPad.
   */
  const loop = useRef({
    stream: null as MediaStream | null,
    landmarker: null as FaceLandmarker | null,
    interval: null as ReturnType<typeof setInterval> | null,
    prepTimer: null as ReturnType<typeof setTimeout> | null,
    timeout: null as ReturnType<typeof setTimeout> | null,
    hintTimer: null as ReturnType<typeof setTimeout> | null,
    step: "prep" as "prep" | "position" | "hold",
    holdFrames: 0,
    bestScore: Infinity,
    bestFrame: null as string | null,
    framesSinceImprovement: 0,
    excellent: false,
    blinkPassed: false,
    eyesClosed: false,
    captured: false,
    shownHint: "",
    pendingHint: "",
    generation: 0,
  });

  const stop = useCallback(() => {
    const l = loop.current;
    if (l.interval) clearInterval(l.interval);
    if (l.prepTimer) clearTimeout(l.prepTimer);
    if (l.timeout) clearTimeout(l.timeout);
    if (l.hintTimer) clearTimeout(l.hintTimer);
    l.interval = l.prepTimer = l.timeout = l.hintTimer = null;
    l.stream?.getTracks().forEach((t) => t.stop());
    l.stream = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const snapshot = useCallback((): string => {
    const video = videoRef.current;
    if (!video) return "";
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.9);
  }, []);

  /**
   * Подсказка с задержкой: одиночный кадр-выброс не должен мигать текстом.
   *
   * Таймер перезапускается, только когда подсказка СМЕНИЛАСЬ, а не на каждом
   * кадре: цикл зовёт это десять раз в секунду, и перезапуск на каждом вызове
   * не дал бы таймеру сработать никогда — новая подсказка так и не появлялась.
   */
  const showHint = useCallback((hint: Hint, fallback: string) => {
    const l = loop.current;
    const text = hint?.text ?? fallback;
    if (text === l.pendingHint) return;
    l.pendingHint = text;
    if (l.hintTimer) clearTimeout(l.hintTimer);
    if (text === l.shownHint) {
      l.hintTimer = null;
      return;
    }
    l.hintTimer = setTimeout(() => {
      l.hintTimer = null;
      l.shownHint = text;
      setState((s) => ({ ...s, hint: text, tone: hint?.tone ?? "neutral" }));
    }, CONFIG.hintDebounceMs);
  }, []);

  const capture = useCallback(() => {
    const l = loop.current;
    if (l.captured) return;
    l.captured = true;
    const image = l.bestFrame || snapshot();
    stop();
    setState((s) => ({
      ...s,
      phase: "done",
      holdPercent: 100,
      hint: TEXT.done,
      tone: "success",
    }));
    onCapturedRef.current(image);
  }, [snapshot, stop]);

  const tick = useCallback(() => {
    const l = loop.current;
    const video = videoRef.current;
    if (!video || !l.landmarker || video.readyState < 2 || l.captured) return;

    const result = l.landmarker.detectForVideo(video, performance.now());
    const faces = result.faceLandmarks ?? [];
    const shapes = result.faceBlendshapes ?? [];

    // Главное лицо — самое центральное: MediaPipe не обещает, что первое в
    // списке — то, что в овале.
    let primary = 0;
    for (let i = 1; i < faces.length; i++) {
      const n = faces[i]![NOSE_TIP]!;
      const p = faces[primary]![NOSE_TIP]!;
      if (Math.abs(n.x - 0.5) + Math.abs(n.y - 0.5) < Math.abs(p.x - 0.5) + Math.abs(p.y - 0.5)) {
        primary = i;
      }
    }
    const lm = faces[primary];
    const bs = shapes[primary];

    if (l.step === "prep") return;

    // Второе лицо В ОВАЛЕ — стоп. Бэкенд кадр с двумя лицами всё равно
    // отклонит, и сказать это до отправки честнее. Человек сбоку не мешает.
    if (faces.filter(isInOval).length > 1) {
      l.holdFrames = 0;
      l.eyesClosed = false;
      showHint({ text: TEXT.multipleFaces, tone: "warn" }, "");
      setState((s) => (s.phase === "position" ? s : { ...s, phase: "position", holdPercent: 0 }));
      return;
    }

    if (!lm) {
      l.holdFrames = 0;
      showHint(positionHint(undefined), "");
      return;
    }

    const positioned = isInOval(lm) && isStraight(lm);

    if (positioned) {
      const score = frameScore(lm, bs);
      if (score < l.bestScore) {
        l.bestScore = score;
        l.bestFrame = snapshot();
        l.framesSinceImprovement = 0;
      } else {
        l.framesSinceImprovement++;
      }
      if (l.bestScore <= CONFIG.excellentScoreThreshold) l.excellent = true;
    }

    if (l.step === "position") {
      if (!positioned) {
        l.eyesClosed = false;
        showHint(positionHint(lm), TEXT.placeFace);
        setState((s) => (s.phase === "position" ? s : { ...s, phase: "position" }));
        return;
      }
      if (requireBlink && !l.blinkPassed) {
        const closed = eyesClosedScore(bs);
        if (!l.eyesClosed && closed > CONFIG.blinkClosedThreshold) l.eyesClosed = true;
        else if (l.eyesClosed && closed < CONFIG.blinkOpenThreshold) l.blinkPassed = true;
        showHint({ text: TEXT.blink, tone: "info" }, "");
        setState((s) => (s.phase === "blink" ? s : { ...s, phase: "blink" }));
        return;
      }
      l.step = "hold";
      l.holdFrames = 0;
      showHint(null, TEXT.lookStraight);
      setState((s) => ({ ...s, phase: "hold", holdPercent: 0 }));
      return;
    }

    // step === "hold"
    if (!positioned) {
      l.holdFrames = 0;
      showHint(positionHint(lm), TEXT.lookStraight);
      setState((s) => (s.holdPercent === 0 ? s : { ...s, holdPercent: 0 }));
      return;
    }
    showHint(null, TEXT.lookStraight);
    l.holdFrames++;
    // Хороший кадр уже есть или лучше не становится — хватает короткой выдержки.
    const target =
      l.excellent || l.framesSinceImprovement >= CONFIG.plateauFrames
        ? HOLD_FRAMES_MIN
        : HOLD_FRAMES_FULL;
    const percent = Math.min(100, Math.round((l.holdFrames / target) * 100));
    setState((s) => (s.holdPercent === percent ? s : { ...s, holdPercent: percent }));
    if (l.holdFrames >= target) capture();
  }, [capture, requireBlink, showHint, snapshot]);

  const start = useCallback(async () => {
    stop();
    const l = loop.current;
    const generation = ++l.generation;
    Object.assign(l, {
      step: "prep",
      holdFrames: 0,
      bestScore: Infinity,
      bestFrame: null,
      framesSinceImprovement: 0,
      excellent: false,
      blinkPassed: false,
      eyesClosed: false,
      captured: false,
      shownHint: "",
      pendingHint: "",
    });
    setState({
      phase: "loading",
      loadProgress: 0,
      holdPercent: 0,
      hint: TEXT.loadingCamera,
      tone: "neutral",
      error: null,
    });

    const fail = (error: string) => {
      stop();
      setState((s) => ({ ...s, phase: "error", error, tone: "warn" }));
    };

    // getUserMedia есть только в защищённом контексте: по http://192.168.x.x
    // `navigator.mediaDevices` просто нет, и ошибка была бы безликой.
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      fail(TEXT.insecure);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      });
      if (generation !== l.generation) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      l.stream = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        // iOS Safari: без playsInline/muted видео уходит в полноэкранный плеер.
        video.playsInline = true;
        video.muted = true;
        await video.play();
      }
    } catch {
      fail(TEXT.cameraError);
      return;
    }

    setState((s) => ({ ...s, hint: TEXT.loadingModel }));
    try {
      l.landmarker = await getFaceLandmarker((pct) =>
        setState((s) => ({ ...s, loadProgress: pct })),
      );
    } catch (error) {
      console.error("[FaceCapture] MediaPipe init failed:", error);
      fail(TEXT.initError);
      return;
    }
    if (generation !== l.generation) return;

    setState((s) => ({ ...s, phase: "prep", loadProgress: 100, hint: TEXT.preparing }));
    l.shownHint = l.pendingHint = TEXT.preparing;
    l.interval = setInterval(tick, CONFIG.detectionIntervalMs);
    l.prepTimer = setTimeout(() => {
      l.step = "position";
      setState((s) => ({ ...s, phase: "position", hint: TEXT.placeFace, tone: "neutral" }));
      l.shownHint = l.pendingHint = TEXT.placeFace;
    }, CONFIG.prepDurationMs);
    l.timeout = setTimeout(() => {
      if (!l.captured) fail(TEXT.timeout);
    }, CONFIG.detectionTimeoutMs);
  }, [stop, tick]);

  useEffect(
    () => () => {
      loop.current.generation++;
      stop();
    },
    [stop],
  );

  return { videoRef, state, start, stop };
}
